import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, Gift, Minus, Plus, ShieldCheck, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/format";
import { LockLine } from "@/components/store/CartDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function offerLabel(o) {
  if (o.type === "percentage") return `${o.value}% off${o.min_order ? ` on orders above ${inr(o.min_order)}` : ""}`;
  if (o.type === "flat") return `${inr(o.value)} off${o.min_order ? ` on orders above ${inr(o.min_order)}` : ""}`;
  return "Free insured shipping";
}

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, coupon, setCoupon, hasPersonalised, toggleItemGift } = useCart();
  const { config, offers } = useStore();
  const [code, setCode] = useState(coupon?.code || "");
  const [applying, setApplying] = useState(false);
  const [showOffers, setShowOffers] = useState(false);
  const navigate = useNavigate();
  const gstRate = config?.gst_rate ?? 3;
  const wrapFee = config?.gift_wrap_fee || 0;

  const applyCode = async (c) => {
    const val = (c ?? code).trim();
    if (!val) return;
    setApplying(true);
    try {
      const r = await api.post("/coupons/validate", { code: val, subtotal });
      setCoupon(r.data);
      setCode(r.data.code);
      toast.success(`Coupon ${r.data.code} applied`);
    } catch (e) {
      setCoupon(null);
      toast.error(errMsg(e));
    } finally {
      setApplying(false);
    }
  };

  const discount = coupon?.discount || 0;
  const threshold = config?.free_shipping_threshold || 0;
  const freeShip = coupon?.free_shipping || (threshold > 0 && subtotal - discount >= threshold) || !(config?.shipping_rates?.[0]?.price);
  const shippingEst = freeShip ? 0 : (config?.shipping_rates?.[0]?.price ?? 0);
  const itemWrapCount = items.reduce((s, i) => s + (i.gift_wrap ? 1 : 0), 0);
  const itemWrapFee = itemWrapCount * wrapFee;
  const total = subtotal - discount + shippingEst + itemWrapFee;
  const saved = discount + (freeShip && threshold > 0 && shippingEst === 0 ? (config?.shipping_rates?.[0]?.price || 0) : 0);
  const gap = threshold > 0 && !freeShip ? threshold - (subtotal - discount) : 0;

  if (items.length === 0) {
    return (
      <div className="container-x py-24 text-center" data-testid="cart-page-empty">
        <h1 className="font-heading text-3xl font-semibold">Nothing here yet</h1>
        <p className="text-stone-500 mt-2">Start with something small — a coin, a pendant, a first piece.</p>
        <Button className="mt-6 bg-stone-900 hover:bg-brand text-white btn-press rounded-none" onClick={() => navigate("/shop")} data-testid="cart-empty-shop-btn">
          Explore Jewellery
        </Button>
      </div>
    );
  }

  return (
    <div className="container-x py-8" data-testid="cart-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Cart</h1>
      {gap > 0 && (
        <div className="mt-4 border border-amber-200 bg-amber-50 px-4 py-3" data-testid="freeship-gap-banner">
          <p className="text-sm text-amber-900">
            <Truck size={15} className="inline mr-1.5 -mt-0.5" />
            Add <strong>{inr(gap)}</strong> more for <strong>FREE insured shipping</strong>
          </p>
          <div className="h-1.5 bg-amber-100 mt-2 overflow-hidden">
            <div className="h-full bg-brand transition-[width]" style={{ width: `${Math.min(100, ((subtotal - discount) / threshold) * 100)}%` }} />
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2 space-y-3">
          {items.map((item) => (
            <div key={item.key} className="flex gap-4 border border-stone-200 bg-white p-4" data-testid={`cart-page-item-${item.sku}`}>
              <Link to={`/products/${item.slug}`}>
                {item.image
                  ? <img src={item.image} alt={item.title} className="h-24 w-24 object-cover bg-stone-50" />
                  : <div className="h-24 w-24 bg-[#FBF8F3] border border-stone-200 flex items-center justify-center" aria-hidden="true"><span className="font-heading text-brand/40 text-lg">S</span></div>}
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/products/${item.slug}`} className="font-medium text-sm hover:underline underline-offset-4">{item.title}</Link>
                <p className="text-xs font-mono text-stone-500 mt-1">{item.variant_label}{item.design ? ` · ${item.design}` : ""}{item.size ? ` · Size ${item.size}` : ""}{item.carats ? ` · ${item.carats} ct` : ""} · {item.purity} {item.metal} · {item.net_weight}g</p>
                <Link to={`/products/${item.slug}`} className="text-[11px] text-brand underline underline-offset-2 mt-0.5 inline-block" data-testid={`cart-edit-${item.sku}`}>Edit configuration</Link>
                {item.personalisation?.summary && (
                  <p className="text-xs text-brand mt-1" data-testid={`cart-pers-${item.sku}`}>✦ {item.personalisation.summary} (non-returnable)</p>
                )}
                <LockLine item={item} className="mt-1" />
                <p className="text-[10px] font-mono text-stone-400 mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`cart-trust-${item.sku}`}>
                  <span className="inline-flex items-center gap-1"><BadgeCheck size={11} className="text-brand" />BIS HALLMARKED</span>
                  <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-brand" />FULLY INSURED</span>
                  <span className="inline-flex items-center gap-1"><Truck size={11} className="text-brand" />DISCREET PACKAGING</span>
                </p>
                <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                  <div className="flex items-center border border-stone-200">
                    <button className="p-2 hover:bg-stone-100" onClick={() => updateQty(item.key, item.qty - 1)} data-testid={`cart-page-minus-${item.sku}`}><Minus size={14} /></button>
                    <span className="px-3 font-mono text-sm" data-testid={`cart-page-qty-${item.sku}`}>{item.qty}</span>
                    <button className="p-2 hover:bg-stone-100" onClick={() => updateQty(item.key, item.qty + 1)} data-testid={`cart-page-plus-${item.sku}`}><Plus size={14} /></button>
                  </div>
                  <p className="font-heading font-semibold">{inr(item.locked_price * item.qty)}</p>
                </div>
                <label className="flex items-center gap-2 mt-2 text-xs text-stone-600 cursor-pointer" data-testid={`cart-item-wrap-${item.sku}`}>
                  <input type="checkbox" checked={!!item.gift_wrap} onChange={() => toggleItemGift(item.key)} />
                  <Gift size={12} className="text-brand" /> Wrap this piece as a gift {wrapFee ? `(+${inr(wrapFee)})` : ""}
                </label>
              </div>
              <button className="self-start text-stone-400 hover:text-red-600" onClick={() => removeItem(item.key)} data-testid={`cart-page-remove-${item.sku}`} aria-label="Remove">
                <Trash2 size={17} />
              </button>
            </div>
          ))}
          {hasPersonalised && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="cart-personalised-notice">
              Your cart contains a personalised piece — crafted to order and non-returnable.
            </p>
          )}
        </div>

        <aside className="border border-stone-200 bg-white p-6 h-fit" data-testid="cart-summary">
          <h2 className="font-heading font-semibold text-lg">Order Summary</h2>
          <div className="flex gap-2 mt-4">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="h-10 font-mono" data-testid="coupon-input" />
            <Button variant="outline" onClick={() => applyCode()} disabled={applying} className="h-10 btn-press" data-testid="coupon-apply-btn">Apply</Button>
          </div>
          {offers?.length > 0 && (
            <div className="mt-2">
              <button className="text-xs text-brand underline underline-offset-2" onClick={() => setShowOffers(!showOffers)} data-testid="coupon-list-toggle">
                {showOffers ? "Hide available offers" : `View ${offers.length} available offer${offers.length > 1 ? "s" : ""}`}
              </button>
              {showOffers && (
                <ul className="mt-2 space-y-1.5" data-testid="coupon-list">
                  {offers.map((o) => (
                    <li key={o.code} className="flex items-center justify-between border border-dashed border-stone-300 px-3 py-2 text-xs">
                      <span><strong className="font-mono">{o.code}</strong> — {offerLabel(o)}</span>
                      <button className="text-brand font-medium underline underline-offset-2" onClick={() => applyCode(o.code)} disabled={applying} data-testid={`coupon-pick-${o.code}`}>Apply</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {coupon && (
            <div className="flex items-center justify-between mt-2 text-sm text-emerald-700" data-testid="coupon-applied">
              <span>{coupon.code} applied</span>
              <button className="text-xs underline" onClick={() => { setCoupon(null); setCode(""); }} data-testid="coupon-remove-btn">Remove</button>
            </div>
          )}
          {saved > 0 && (
            <p className="mt-3 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="cart-savings">
              You're saving {inr(saved)} on this order
            </p>
          )}
          <dl className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-stone-600">Subtotal</dt><dd data-testid="summary-subtotal">{inr(subtotal)}</dd></div>
            {discount > 0 && <div className="flex justify-between text-emerald-700"><dt>Discount</dt><dd data-testid="summary-discount">−{inr(discount)}</dd></div>}
            <div className="flex justify-between">
              <dt className="text-stone-600">Insured shipping</dt>
              <dd data-testid="summary-shipping">{shippingEst === 0 ? <span className="text-emerald-700 font-medium">FREE</span> : inr(shippingEst)}</dd>
            </div>
            {itemWrapFee > 0 && <div className="flex justify-between"><dt className="text-stone-600">Gift wrap ({itemWrapCount} item{itemWrapCount > 1 ? "s" : ""})</dt><dd data-testid="summary-gift-wrap">{inr(itemWrapFee)}</dd></div>}
            <div className="flex justify-between text-xs text-stone-500"><dt>Prices include GST @ {gstRate}%</dt></div>
          </dl>
          <div className="border-t border-stone-200 mt-4 pt-4 flex justify-between font-heading font-semibold text-lg">
            <span>Total</span>
            <span data-testid="summary-total">{inr(total)}</span>
          </div>
          <Button className="w-full mt-5 h-11 bg-stone-900 hover:bg-brand text-white btn-press rounded-none" onClick={() => navigate("/checkout")} data-testid="cart-checkout-btn">
            Proceed to Checkout
          </Button>
          <p className="text-[11px] text-stone-500 mt-3 text-center">Secure checkout · UPI, Cards, NetBanking · Prepaid only</p>
        </aside>
      </div>
    </div>
  );
}
